import { useTranslation } from 'react-i18next';

import { send } from '@actual-app/core/platform/client/connection';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

import { addNotification } from '#notifications/notificationsSlice';
import { useDispatch } from '#redux';

import { attachmentQueries } from './queries';

export type AttachmentMutationFile = {
  name: string;
  type: string;
  contents: ArrayBuffer;
};

type AddAttachmentPayload = {
  transactionId: string;
  file: AttachmentMutationFile;
};

type ReplaceAttachmentPayload = {
  attachmentId: string;
  transactionId: string;
  file: AttachmentMutationFile;
};

type DeleteAttachmentPayload = {
  attachmentId: string;
  transactionId: string;
};

type RetryAttachmentPayload = {
  attachmentId: string;
  transactionId: string;
};

type RestoreAttachmentPayload = {
  attachmentId: string;
  transactionId: string;
};

type EmptyAttachmentTrashPayload = {
  attachmentIds: string[];
};

type RetryAttachmentPurgePayload = {
  attachmentIds?: string[];
};

export const attachmentMutationCommands = {
  add: async (
    sendFn: typeof send,
    { transactionId, file }: AddAttachmentPayload,
  ) =>
    sendFn('transaction-attachment-add', {
      transactionId,
      filename: file.name,
      declaredMimeType: file.type || undefined,
      contents: file.contents,
    }),
  replace: async (
    sendFn: typeof send,
    { attachmentId, file }: ReplaceAttachmentPayload,
  ) =>
    sendFn('transaction-attachment-replace', {
      id: attachmentId,
      filename: file.name,
      declaredMimeType: file.type || undefined,
      contents: file.contents,
    }),
  remove: async (
    sendFn: typeof send,
    { attachmentId }: DeleteAttachmentPayload,
  ) => sendFn('transaction-attachment-delete', { id: attachmentId }),
  retry: async (
    sendFn: typeof send,
    { attachmentId }: RetryAttachmentPayload,
  ) => sendFn('transaction-attachment-retry', { id: attachmentId }),
  restore: async (
    sendFn: typeof send,
    { attachmentId }: RestoreAttachmentPayload,
  ) => sendFn('transaction-attachment-restore', { id: attachmentId }),
  emptyTrash: async (
    sendFn: typeof send,
    { attachmentIds }: EmptyAttachmentTrashPayload,
  ) => sendFn('transaction-attachments-empty-trash', { attachmentIds }),
  retryPurge: async (
    sendFn: typeof send,
    { attachmentIds }: RetryAttachmentPurgePayload,
  ) => sendFn('transaction-attachments-purge-retry', { attachmentIds }),
};

function invalidateAttachmentList(
  queryClient: QueryClient,
  transactionId: string,
) {
  return queryClient.invalidateQueries({
    queryKey: attachmentQueries.list(transactionId).queryKey,
  });
}

function invalidateAttachmentLibrary(queryClient: QueryClient) {
  return queryClient.invalidateQueries({
    queryKey: attachmentQueries.libraryRoot(),
  });
}

function useAttachmentMutationError(message: string) {
  const dispatch = useDispatch();

  return (error: Error) => {
    dispatch(
      addNotification({
        notification: {
          type: 'error',
          message,
          pre: error.message,
        },
      }),
    );
  };
}

export function useAddAttachmentMutation() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const onError = useAttachmentMutationError(
    t('There was an error adding the attachment. Please try again.'),
  );

  return useMutation({
    mutationFn: (payload: AddAttachmentPayload) =>
      attachmentMutationCommands.add(send, payload),
    onSuccess: (_attachment, { transactionId }) =>
      invalidateAttachmentList(queryClient, transactionId),
    onError,
  });
}

export function useReplaceAttachmentMutation() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const onError = useAttachmentMutationError(
    t('There was an error replacing the attachment. Please try again.'),
  );

  return useMutation({
    mutationFn: (payload: ReplaceAttachmentPayload) =>
      attachmentMutationCommands.replace(send, payload),
    onSuccess: (_attachment, { transactionId }) =>
      invalidateAttachmentList(queryClient, transactionId),
    onError,
  });
}

export function useDeleteAttachmentMutation() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const onError = useAttachmentMutationError(
    t('There was an error deleting the attachment. Please try again.'),
  );

  return useMutation({
    mutationFn: (payload: DeleteAttachmentPayload) =>
      attachmentMutationCommands.remove(send, payload),
    onSuccess: (_result, { transactionId }) =>
      invalidateAttachmentList(queryClient, transactionId),
    onError,
  });
}

export function useRetryAttachmentMutation() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const onError = useAttachmentMutationError(
    t('There was an error retrying the attachment. Please try again.'),
  );

  return useMutation({
    mutationFn: (payload: RetryAttachmentPayload) =>
      attachmentMutationCommands.retry(send, payload),
    onSuccess: (_result, { transactionId }) =>
      invalidateAttachmentList(queryClient, transactionId),
    onError,
  });
}

export function useRestoreAttachmentMutation() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const onError = useAttachmentMutationError(
    t('There was an error restoring the attachment. Please try again.'),
  );

  return useMutation({
    mutationFn: (payload: RestoreAttachmentPayload) =>
      attachmentMutationCommands.restore(send, payload),
    onSuccess: async (_result, { transactionId }) => {
      await invalidateAttachmentList(queryClient, transactionId);
      await invalidateAttachmentLibrary(queryClient);
    },
    onError,
  });
}

export function useEmptyAttachmentTrashMutation() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const onError = useAttachmentMutationError(
    t('There was an error emptying attachment Trash. Please try again.'),
  );

  return useMutation({
    mutationFn: (payload: EmptyAttachmentTrashPayload) =>
      attachmentMutationCommands.emptyTrash(send, payload),
    onSuccess: () => invalidateAttachmentLibrary(queryClient),
    onError,
  });
}

export function useRetryAttachmentPurgeMutation() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const onError = useAttachmentMutationError(
    t('There was an error retrying attachment cleanup. Please try again.'),
  );

  return useMutation({
    mutationFn: (payload: RetryAttachmentPurgePayload) =>
      attachmentMutationCommands.retryPurge(send, payload),
    onSuccess: () => invalidateAttachmentLibrary(queryClient),
    onError,
  });
}
